/**
 * Recognition API hooks — TanStack Query layer over /api/recognition.
 *
 * Any authenticated user can give recognition. Employees only see public
 * recognition (or their own) — enforced server-side.
 * Admins can edit/delete any; givers can edit their own.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { Recognition, Paginated } from '@shared/types';
import {
  recognitionCreateInput,
  recognitionUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type RecognitionCreateBody = z.input<typeof recognitionCreateInput>;
export type RecognitionUpdateBody = z.input<typeof recognitionUpdateInput>;

export const recognitionKeys = {
  all: ['recognition'] as const,
  list: (params?: { employeeId?: string }) =>
    [...recognitionKeys.all, 'list', params ?? {}] as const,
};

/** List recognition entries, optionally scoped to an employee. */
export function useRecognition(params?: { employeeId?: string }) {
  return useQuery({
    queryKey: recognitionKeys.list(params),
    queryFn: () =>
      api.get<Paginated<Recognition>>(`/api/recognition${qs(params)}`),
  });
}

/** Create a recognition entry (any authenticated user). `givenBy` is set server-side. */
export function useCreateRecognition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RecognitionCreateBody) =>
      api.post<Recognition>('/api/recognition', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
    },
  });
}

/** Update recognition (Admin or original giver). */
export function useUpdateRecognition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RecognitionUpdateBody }) =>
      api.put<Recognition>(`/api/recognition/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
    },
  });
}

/** Delete recognition (Admin, or the original giver). */
export function useDeleteRecognition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/recognition/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
    },
  });
}
